import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import type {PredictionBatchSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import {
  aaSequenceId,
  buildPredictionBatchName,
  moleculeId,
  proteinId,
  submitBulkPrediction,
} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import {
  getComponentValidationResult,
  getValidationErrorMessages,
} from 'model-hub/scientific-models/input-forms/structure-prediction/ComponentUtils';
import {NO_MODELS_INSTALLED_BANNER_MESSAGE} from 'model-hub/scientific-models/NoModelsInstalledEmptyState';
import {
  getMaxAminoAcidCount,
  getMinAminoAcidCount,
  getUseMSAForSubmission,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {MolstarViewableExternalFile} from 'molbio/molstar/constants';

import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';

interface BaseStructureInfo {
  structureFile: MolstarViewableExternalFile;
  model: string;
  modelVersion: string;
  structureFileOutOfDate: boolean;
  createdAt: string;
  coordinateStructureId: string;
  componentIds: Array<string>;
}

/**
 * A structure backed by an external file whose `structureFile.url` must be re-signed on demand before
 * the Mol* viewer can load it; `versionId` identifies the owning entity version used to re-sign.
 */
export interface ExternalFileStructureInfo extends BaseStructureInfo {
  preSigned: false;
  versionId: number;
}

/**
 * A structure whose `structureFile.url` is already a signed download URL (e.g. a prediction job's
 * standardized output), so it renders directly and needs no `versionId` for re-signing.
 */
export interface PreSignedStructureInfo extends BaseStructureInfo {
  preSigned: true;
}

export type AvailableStructureInfo = ExternalFileStructureInfo | PreSignedStructureInfo;

export const USER_VERIFICATION_STATUS: DocumentNode = gql`
  query UserVerificationStatusQuery @skipAudit @engComponent(name: MODEL_HUB) {
    currentUser {
      ... on UserNode {
        id
        academic
        institution {
          id
          verified
        }
        verified
      }
    }
  }
`;

export const ACADEMIC_VERIFICATION_MESSAGE =
  'Structure prediction is only available to verified users. Please verify your email to continue.';

export function isAcademicUserBlocked(
  currentUser: {
    academic?: boolean;
    verified?: boolean;
    institution?: {verified?: boolean} | null;
  } | null
): boolean {
  if (!BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC) {
    return false;
  }
  if (currentUser?.verified) {
    return false;
  }
  if (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT && currentUser?.verified) {
    return false;
  }
  return true;
}

export function getRunBlockingMessage(
  aminoAcids: string,
  permissions: Permissions,
  remainingRuns: number,
  model: ScientificModelNode
): string | null {
  const validationResult = getComponentValidationResult(
    {
      proteinComponents: {
        aminoAcids,
        count: 1,
        moleculeCount: 1,
        numHeavyAtoms: 0,
        entityType: ModelTypeConstants.AA_SEQUENCE,
        permissions,
      },
    },
    model
  );
  if (remainingRuns <= 0) {
    return 'You do not have any remaining structure prediction runs.';
  } else if (validationResult != null) {
    return getValidationErrorMessages(validationResult, true).join('\n');
  }
  return null;
}

interface BaseInputComponent {
  id: string;
  folderId: string;
  permissions: Permissions;
}
export interface ProteinInput extends BaseInputComponent {
  chains: Array<string>;
}

export interface AaSequenceInput extends BaseInputComponent {
  aminoAcids: string;
}

export type InputComponent = ProteinInput | AaSequenceInput;

export function isProteinInput(inputComponent: InputComponent): inputComponent is ProteinInput {
  return 'chains' in inputComponent;
}

const INSUFFICIENT_PERMISSIONS_MESSAGE =
  'You do not have sufficient permissions to create a run on this entity.';

/**
 * Resolves the message explaining why structure-prediction runs are disabled, or undefined when runs
 * are allowed. Terms-and-conditions restrictions take precedence over missing models, which take
 * precedence over insufficient entity permissions.
 */
export function getRunDisabledMessage({
  hasAvailableModels,
  structurePredictionEditable,
}: {
  hasAvailableModels: boolean;
  structurePredictionEditable: boolean;
}): string | undefined {
  const tacMessage = getScientificAIDisabledMessage();
  const noModelsMessage = hasAvailableModels ? null : NO_MODELS_INSTALLED_BANNER_MESSAGE;
  const entityMessage = structurePredictionEditable ? null : INSUFFICIENT_PERMISSIONS_MESSAGE;
  return tacMessage ?? noModelsMessage ?? entityMessage ?? undefined;
}

/**
 * Partitions models by whether the given sequence length is within each model's supported range.
 * A model is invalid when the length is below its minimum or above its maximum amino-acid count;
 * `invalidModelNames` collects the display names of the excluded models so callers can explain which
 * models were skipped.
 */
export function filterModelsBySequenceLength(
  models: ReadonlyArray<ScientificModelNode>,
  sequenceLength: number
): {validModels: Array<ScientificModelNode>; invalidModelNames: Array<string>} {
  const invalidModelNames: Array<string> = [];
  const validModels = models.filter((model) => {
    const minCount = getMinAminoAcidCount(model);
    const maxCount = getMaxAminoAcidCount(model);
    if (minCount != null && sequenceLength < minCount) {
      invalidModelNames.push(model.displayName);
      return false;
    }
    if (sequenceLength > maxCount) {
      invalidModelNames.push(model.displayName);
      return false;
    }
    return true;
  });
  return {validModels, invalidModelNames};
}

export const REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY: DocumentNode = gql`
  query RemainingExecutionsAndTenantNameQuery @skipAudit @engComponent(name: MODEL_HUB) {
    remainingAlphafoldExecutions
    modelHubExecutionLimit
    currentTenant {
      ... on TenantNode {
        id
        displayName
      }
    }
  }
`;

export interface RequestRunParams {
  aaSequenceIds: Array<string>;
  moleculeIds: Array<string>;
  proteinIds: Array<string>;
  primaryComponentId: string | undefined;
  // Version id of the primary protein component, used to evict that version's cached prediction jobs
  // (`proteinVersionPredictionJobs` is keyed by version). Only set for the protein-entity flow.
  primaryComponentVersionId?: string;
  folderId: string;
  remainingExecutionsAndTenantNameData: RemainingExecutionsAndTenantNameQuery;
  selectedModel: ScientificModelNode;
  msaEnabled: boolean;
  source?: PredictionBatchSource;
}

export async function requestRun({
  aaSequenceIds,
  moleculeIds,
  proteinIds,
  primaryComponentId,
  primaryComponentVersionId,
  folderId,
  remainingExecutionsAndTenantNameData,
  selectedModel,
  msaEnabled,
  source,
}: RequestRunParams): Promise<void> {
  if (!selectedModel.capabilities.supportsMultimers && proteinIds.length > 0) {
    throw new Error('proteinId components are only supported for multimer-capable models');
  }

  const predictionConfigs = selectedModel.capabilities.supportsMultimers
    ? [
        {
          components: [
            ...aaSequenceIds.map((id) => aaSequenceId(id)),
            ...proteinIds.map((id) => proteinId(id)),
            ...moleculeIds.map((id) => moleculeId(id)),
          ],
        },
      ]
    : [
        ...aaSequenceIds.map((id) => ({component: aaSequenceId(id)})),
        ...moleculeIds.map((id) => ({component: moleculeId(id)})),
      ];
  await submitBulkPrediction({
    folderId,
    model: selectedModel,
    predictionConfigs,
    modelParams: {useMsa: getUseMSAForSubmission(selectedModel, msaEnabled)},
    source,
    name: buildPredictionBatchName(selectedModel),
  });

  // Update Apollo cache to reflect the new prediction in progress
  ApolloWrappers.writeQuery(
    {query: REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY},
    {
      ...remainingExecutionsAndTenantNameData,
      remainingAlphafoldExecutions: remainingExecutionsAndTenantNameData.remainingAlphafoldExecutions - 1,
    }
  );
  const primaryComponentType =
    primaryComponentId != null && aaSequenceIds.includes(primaryComponentId)
      ? ModelTypeConstants.AA_SEQUENCE
      : primaryComponentId != null && proteinIds.includes(primaryComponentId)
        ? ModelTypeConstants.PROTEIN
        : undefined;

  // Since the list of in progress predictions is not just a boolean,
  // we need to evict the list because we do not have all of the information here to update the cache locally
  if (primaryComponentId != null && primaryComponentType === ModelTypeConstants.AA_SEQUENCE) {
    getApolloClient().cache.evict({
      id: `AaSequenceNode:${primaryComponentId}`,
      fieldName: 'inProgressStructurePredictionJobs',
    });
  } else if (primaryComponentType === ModelTypeConstants.PROTEIN) {
    assert(primaryComponentVersionId != null, 'primaryComponentVersionId is required for protein entities');
    getApolloClient().cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'proteinVersionPredictionJobs',
      args: {proteinVersionId: primaryComponentVersionId},
    });
  }
}

export function getCoordinateStructureId(coordinateStructure: AvailableStructureInfo): string {
  return coordinateStructure.structureFile.id;
}
